import React, {useEffect, useRef, useState} from "react";
import "./CodeInput.scss";

const LENGTH = 6;

/**
 * Six single-digit boxes that behave the way people expect a one-time code to:
 * type to advance, backspace to go back, paste to fill the whole thing.
 */
export default function CodeInput({onComplete, disabled, isDark, resetSignal}) {
  const [digits, setDigits] = useState(Array(LENGTH).fill(""));
  const inputRefs = useRef([]);

  // Clear the boxes whenever the parent reports a failed attempt.
  useEffect(() => {
    if (resetSignal === 0) return;
    setDigits(Array(LENGTH).fill(""));
    const first = inputRefs.current[0];
    if (first) first.focus();
  }, [resetSignal]);

  const commitIfComplete = next => {
    if (next.every(digit => digit !== "")) {
      onComplete(next.join(""));
    }
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");
    if (!value) return;

    const next = [...digits];
    // A multi-character value means the browser autofilled or the user pasted.
    value.split("").forEach((digit, offset) => {
      if (index + offset < LENGTH) next[index + offset] = digit;
    });
    setDigits(next);

    const nextFocus = Math.min(index + value.length, LENGTH - 1);
    const target = inputRefs.current[nextFocus];
    if (target) target.focus();

    commitIfComplete(next);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...digits];

      if (next[index]) {
        next[index] = "";
      } else if (index > 0) {
        next[index - 1] = "";
        const previous = inputRefs.current[index - 1];
        if (previous) previous.focus();
      }
      setDigits(next);
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1].focus();
    }

    if (event.key === "ArrowRight" && index < LENGTH - 1) {
      event.preventDefault();
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = event => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    event.preventDefault();
    const next = Array(LENGTH).fill("");
    pasted
      .slice(0, LENGTH)
      .split("")
      .forEach((digit, index) => {
        next[index] = digit;
      });
    setDigits(next);

    const focusIndex = Math.min(pasted.length, LENGTH - 1);
    inputRefs.current[focusIndex].focus();
    commitIfComplete(next);
  };

  return (
    <div className="code-input" role="group" aria-label="6-digit access code">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={element => (inputRefs.current[index] = element)}
          className={isDark ? "code-input-box dark-mode" : "code-input-box"}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          pattern="\d*"
          maxLength={LENGTH}
          value={digit}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${LENGTH}`}
          onChange={event => handleChange(index, event.target.value)}
          onKeyDown={event => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={event => event.target.select()}
        />
      ))}
    </div>
  );
}
